/**
 * @license Copyright (c) 2014-2024, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-oss-license
 */

import VideoLinkConverterPlugin from './VideoLinkConverterPlugin';
import { ClassicEditor } from '@ckeditor/ckeditor5-editor-classic';
import { SimpleUploadAdapter, FileRepository } from '@ckeditor/ckeditor5-upload';
import { CKFinder,CKFinderEditing } from '@ckeditor/ckeditor5-ckfinder';
import { CKFinderUploadAdapter } from '@ckeditor/ckeditor5-adapter-ckfinder';
import { Alignment } from '@ckeditor/ckeditor5-alignment';
import { Autosave } from '@ckeditor/ckeditor5-autosave';
import { Bold, Code, Italic, Strikethrough, Underline } from '@ckeditor/ckeditor5-basic-styles';
import { BlockQuote } from '@ckeditor/ckeditor5-block-quote';
import { CodeBlock } from '@ckeditor/ckeditor5-code-block';
import type { EditorConfig } from '@ckeditor/ckeditor5-core';
import { Essentials } from '@ckeditor/ckeditor5-essentials';
import { FindAndReplace } from '@ckeditor/ckeditor5-find-and-replace';
import { FontBackgroundColor, FontColor, FontFamily, FontSize } from '@ckeditor/ckeditor5-font';
import { Heading } from '@ckeditor/ckeditor5-heading';
import { HtmlEmbed } from '@ckeditor/ckeditor5-html-embed';
import { DataFilter, GeneralHtmlSupport } from '@ckeditor/ckeditor5-html-support';
import {
	AutoImage,
	Image,
    ImageInsert,
	ImageCaption,
	ImageResize,
	ImageStyle,
	ImageToolbar,
	ImageUpload
} from '@ckeditor/ckeditor5-image';
import { Indent, IndentBlock } from '@ckeditor/ckeditor5-indent';
import { AutoLink, Link, LinkEditing, LinkImage } from '@ckeditor/ckeditor5-link';
import { List, ListProperties } from '@ckeditor/ckeditor5-list';
import { MediaEmbed, MediaEmbedToolbar } from '@ckeditor/ckeditor5-media-embed';
import { Paragraph } from '@ckeditor/ckeditor5-paragraph';
import { PasteFromOffice } from '@ckeditor/ckeditor5-paste-from-office';
import { RemoveFormat } from '@ckeditor/ckeditor5-remove-format';
import { SelectAll } from '@ckeditor/ckeditor5-select-all';
import { ShowBlocks } from '@ckeditor/ckeditor5-show-blocks';
import { SourceEditing } from '@ckeditor/ckeditor5-source-editing';
import {
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText
} from '@ckeditor/ckeditor5-special-characters';
import { Style } from '@ckeditor/ckeditor5-style';
import {
	Table,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar
} from '@ckeditor/ckeditor5-table';
import { TextTransformation } from '@ckeditor/ckeditor5-typing';
import { Undo } from '@ckeditor/ckeditor5-undo';
import { WordCount } from '@ckeditor/ckeditor5-word-count';

// You can read more about extending the build with additional plugins in the "Installing plugins" guide.
// See https://ckeditor.com/docs/ckeditor5/latest/installation/plugins/installing-plugins.html for details.

class Editor extends ClassicEditor {
	public static override builtinPlugins = [
        LinkImage,
        ImageInsert,
        VideoLinkConverterPlugin,
        SimpleUploadAdapter,
        FileRepository,
        Alignment,
		AutoImage,
		AutoLink,
		Autosave,
		BlockQuote,
		Bold,
        CKFinder,
        CKFinderEditing,
		CKFinderUploadAdapter,
		Code,
		CodeBlock,
		DataFilter,
		Essentials,
		FindAndReplace,
		FontBackgroundColor,
		FontColor,
		FontFamily,
		FontSize,
		GeneralHtmlSupport,
		Heading,
		HtmlEmbed,
		Image,
		ImageCaption,
		ImageResize,
		ImageStyle,
		ImageToolbar,
		ImageUpload,
		Indent,
		IndentBlock,
		Italic,
		Link,
        LinkEditing,
		List,
		ListProperties,
		MediaEmbed,
		MediaEmbedToolbar,
		Paragraph,
		PasteFromOffice,
		RemoveFormat,
		SelectAll,
		ShowBlocks,
		SourceEditing,
		SpecialCharacters,
		SpecialCharactersArrows,
		SpecialCharactersCurrency,
		SpecialCharactersEssentials,
		SpecialCharactersLatin,
		SpecialCharactersMathematical,
		SpecialCharactersText,
		Strikethrough,
		Style,
		Table,
		TableCellProperties,
		TableColumnResize,
		TableProperties,
		TableToolbar,
		TextTransformation,
		Underline,
		Undo,
		WordCount
	];

	public static override defaultConfig: {
        toolbar: { shouldNotGroupWhenFull: boolean; sticky: boolean; items: string[] };
        image: { toolbar: string[]; imageUpload: { types: string[]; uploadUrl: string } };
        language: string;
        table: { contentToolbar: string[] }
    } = {
		toolbar: {
			items: [
				'heading',
				'|',
				'bold',
				'italic',
				'link',
				'fontBackgroundColor',
				'fontColor',
				'fontFamily',
				'fontSize',
				'underline',
				'strikethrough',
				'specialCharacters',
				'|',
				'bulletedList',
				'numberedList',
				'outdent',
				'indent',
				'alignment',
				'|',
                'ImageInsert',
                'CKFinder',
				'blockQuote',
				'insertTable',
				'mediaEmbed',
				'undo',
				'redo',
				'sourceEditing',
				'showBlocks',
				'htmlEmbed',
				'codeBlock',
				'code',
				'removeFormat',
				'findAndReplace',
				'style'
			],
            shouldNotGroupWhenFull: true,
            sticky: true
		},
		language: 'az',
		image: {
            imageUpload: {
                types: ['jpeg', 'jpg', 'png', 'gif', 'bmp', 'webp', 'tiff'],
                uploadUrl: '{{ route("upload.image") }}'
            },
			toolbar: [
                'imageStyle:alignLeft',
                'imageStyle:alignRight',
                'imageStyle:block',
				'imageStyle:inline',
				'imageStyle:side',
                'toggleImageCaption',
                'imageTextAlternative'
            ]
		},

		table: {
			contentToolbar: [
				'tableColumn',
				'tableRow',
				'mergeTableCells',
				'tableCellProperties',
				'tableProperties'
			]
		}
	};
}

export default Editor;
